import React, { useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setPrescriptions as updatePrescriptions } from "../redux/userSlice";
import { MinorUserContext } from "../context/UserNameProvider";
import Question from "../components/Question";
import MedicationForm from "../components/MedicationForm";
import { Box, FormControl, Typography } from "@mui/material";
import ContinueButton from "../components/ContinueButton";
import { continueButtonSx } from "../lib/uiformatting";
import setPageAndDeferNavigate from "../lib/setPageAndDeferNavigate";

export default function Prescription() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const { firstName, isMinor } = useContext(MinorUserContext);
    const isAcneConcern = user?.concern === "Acne";
    const isHairLossConcern = user?.concern === "Hair loss";
    const isPsoriasisConcern = user?.concern === "Psoriasis";
    const isEczemaConcern = user?.concern === "Eczema / Atopic Dermatitis";

    const [medications, setMedications] = useState(() => {
        if (user?.prescriptions?.length > 0) {
            if (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) {
                // For Acne, Hair loss, Psoriasis, and Eczema users, separate incomplete and complete medications
                return user.prescriptions.filter(med => !med?.name || !med?.duration || !med?.help || (med?.name === "Other" && !med?.customName));
            } else {
                // For non-Acne/Hair loss users, show all medications in the form
                return user.prescriptions.map(med => ({
                    ...(med ?? {}),
                    id: med?.id || `med-${Date.now()}`,
                    timestamp: med?.timestamp || Date.now()
                }));
            }
        } else {
            return [{ name: "", duration: "", help: "", dosage: "", id: `med-${Date.now()}`, timestamp: Date.now() }];
        }
    });
    const [error, setError] = useState("");
    const [completedMedications, setCompletedMedications] = useState(() => {
        if (user?.prescriptions?.length > 0) {
            if (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) {
                // For Acne, Hair loss, Psoriasis, and Eczema users, show completed medications as cards
                return user.prescriptions.filter(med =>
                    med?.name && med?.duration && med?.help && (med?.name !== "Other" || med?.customName)
                  ).map(med => ({
                    ...(med ?? {}),
                    id: med?.id || `med-${Date.now()}`,
                    timestamp: med?.timestamp || Date.now()
                  }));
            } else {
                // For non-Acne/Hair loss users, no completed medications (all in form)
                return [];
            }
        } else {
            return [];
        }
    });
    const [hasIncompleteMedications, setHasIncompleteMedications] = useState(false);

    // Memoize callbacks to prevent unnecessary re-renders
    const handleCompletedMedicationsChange = useCallback((meds) => {
        setCompletedMedications(meds);
    }, []);

    const handleIncompleteMedicationsChange = useCallback((hasIncomplete) => {
        setHasIncompleteMedications(hasIncomplete);
    }, []);

    // Ensure there's always at least one medication for the form
    useEffect(() => {
        if (medications.length === 0) {
            setMedications([{
                name: "",
                duration: "",
                help: "",
                dosage: "",
                id: `med-${Date.now()}`,
                timestamp: Date.now()
            }]);
        }
    }, [medications.length]);

    const handleSubmit = () => {
        if (hasIncompleteMedications) {
            setError("Please complete all medication entries before continuing. Click 'Add [medication name]' for any incomplete entries.");
            return;
        }

        if (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) {
            // For Acne, Hair loss, Psoriasis, and Eczema users, submit completedMedications (cards)
            if (completedMedications.length > 0) {
                dispatch(updatePrescriptions(completedMedications));
                // accutane 
                if (user.concern === "Acne" && (user.address?.state === "CT" || user.address?.state === "MA")) {
                    setPageAndDeferNavigate(dispatch, '/acne-treatment', navigate);
                } else {
                    setPageAndDeferNavigate(dispatch, '/duration', navigate);
                }
            } else {
                setError("Please enter at least one complete medication entry.");
            }
        } else {
            // For non-Acne users, submit all medications from the form
            const completeMedications = medications.filter(med =>
                med.name && med.duration && med.help && (med.name !== "Other" || med.customName)
            );

            if (completeMedications.length > 0) {
                dispatch(updatePrescriptions(completeMedications));
                // accutane 
                if (user.concern === "Acne" && (user.address?.state === "CT" || user.address?.state === "MA")) {
                    setPageAndDeferNavigate(dispatch, '/acne-treatment', navigate);
                } else {
                    setPageAndDeferNavigate(dispatch, '/duration', navigate);
                }
            } else {
                setError("Please enter at least one complete medication entry.");
            }
        }
    };

    const handleBack = () => {
        setPageAndDeferNavigate(dispatch, '/tried-before', navigate);
    };

    const subtitle = isEczemaConcern 
        ? "Your prescription may have been a cream, lotion, ointment, or shampoo"
        : "Select Other to enter a custom medication name";

    return (
        <Question
            onBack={handleBack}
            title={`What ${isMinor ? `has ${firstName}` : "have you"} tried so far?`}
            subtitle={subtitle}
            input={
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    pb: "120px"
                }}>
                    <FormControl sx={{ width: "100%", mb: "2rem" }} required>
                        <MedicationForm
                            medications={medications}
                            setMedications={setMedications}
                            onCompletedMedicationsChange={handleCompletedMedicationsChange}
                            onIncompleteMedicationsChange={handleIncompleteMedicationsChange}
                            initialCompletedMedications={completedMedications}
                        />
                    </FormControl>

                    {error && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            }
            footer={
                <Box
                    sx={{
                        ...continueButtonSx,
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTop: "1px solid",
                        borderTopColor: "grayTones.border",
                        zIndex: 1000,
                        boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
                    }}
                >
                    <ContinueButton
                        onClick={handleSubmit}
                        disabled={
                            (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern)
                                ? (completedMedications?.length === 0 || hasIncompleteMedications)
                                : (completedMedications?.length === 0 || hasIncompleteMedications)
                        }
                        title="Continue"
                        selected={
                            (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern)
                                ? (completedMedications?.length > 0 && !hasIncompleteMedications)
                                : (completedMedications?.length > 0 && !hasIncompleteMedications)
                        }
                    />
                </Box>
            }
        />
    );
}
